import { createKbApi, findSeedPageDef, type KbPage, type KbWiki } from "@/lib/kb-api";
import type { KnowledgeEntryFieldMeta } from "@/lib/hermes-knowledge-entry";
import type { KbSuggestionSnapshot } from "@/lib/hermes-conversation-suggestions";

function fieldMetasFromWikiPage(wiki: KbWiki, page: KbPage): KnowledgeEntryFieldMeta[] {
  const seedDef = findSeedPageDef(wiki, page);
  if (!seedDef) return [];
  return Object.entries(seedDef.properties).map(([key, schema]) => ({
    key,
    label: schema.description ?? key,
  }));
}

function structuredAllEmpty(structured: Record<string, unknown>, fields: KnowledgeEntryFieldMeta[]): boolean {
  if (fields.length === 0) {
    return Object.values(structured).every((value) => {
      if (value == null) return true;
      if (typeof value === "string") return value.trim().length === 0;
      if (Array.isArray(value)) return value.length === 0;
      return false;
    });
  }
  return fields.every((field) => {
    const value = structured[field.key];
    if (value == null) return true;
    if (typeof value === "string") return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    return false;
  });
}

/**
 * Load enterprise KB signals for Hermes empty-state suggestions.
 * Best-effort: failures return null so UI can fall back to generic starters.
 */
export async function loadKbSuggestionSnapshot(
  accountId: string,
): Promise<KbSuggestionSnapshot | null> {
  if (!accountId) return null;

  try {
    const kb = createKbApi(accountId);
    const [{ wikis }, { raw_sources }] = await Promise.all([
      kb.listWikis(),
      kb.listRawSources(),
    ]);

    const enterprise = wikis.find((wiki) => wiki.kind === "enterprise");
    if (!enterprise) {
      return {
        accountId,
        brandId: null,
        emptySeedTitles: [],
        inboxCount: raw_sources.length,
      };
    }

    const { pages } = await kb.listPages(enterprise.id);
    const seedPages = pages.filter((page) => page.is_seed);
    const emptySeedTitles: string[] = [];

    for (const page of seedPages) {
      const fields = fieldMetasFromWikiPage(enterprise, page);
      if (structuredAllEmpty(page.structured ?? {}, fields)) {
        emptySeedTitles.push(page.title);
      }
    }

    const companyProfilePage =
      seedPages.find((page) => page.slug === "company-profile") ?? seedPages[0];

    const companyProfile = companyProfilePage
      ? {
          pageId: companyProfilePage.id,
          wikiId: enterprise.id,
          pageSlug: companyProfilePage.slug,
          fields: fieldMetasFromWikiPage(enterprise, companyProfilePage),
          structured: companyProfilePage.structured ?? {},
        }
      : undefined;

    return {
      accountId,
      brandId: null,
      companyProfile,
      emptySeedTitles,
      inboxCount: raw_sources.length,
    };
  } catch {
    return null;
  }
}
